import { AlertCircle, X } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getSubscriptionInfo, SubscriptionType, isLegacyAccount } from "@/utils/subscriptionUtils";

export const SubscriptionBanner = () => {
  const { businessProfile } = useProfile();
  const [isDismissed, setIsDismissed] = useState(false);

  // Hide for legacy accounts (pre-Stripe)
  if (isLegacyAccount(businessProfile?.accountType, businessProfile?.subscription)) {
    return null;
  }

  if (!businessProfile?.subscription || isDismissed) {
    return null;
  }

  const subscription = businessProfile.subscription;
  const subscriptionInfo = getSubscriptionInfo(subscription);

  // Determine the message to show based on subscription type
  let message = "";
  let showUpgradeButton = false;
  let isInfoBanner = false; // For active trials (blue instead of purple)

  switch (subscriptionInfo.type) {
    case SubscriptionType.ACTIVE_TRIAL:
      if (subscription.trialEnd) {
        const trialEndDate = new Date(subscription.trialEnd.seconds * 1000);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysRemaining <= 7) {
          // Show banner when trial has 7 days or less
          const endDateStr = trialEndDate.toLocaleDateString("sv-SE", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          message = `Din testperiod avslutas den ${endDateStr} (${daysRemaining} ${daysRemaining === 1 ? 'dag' : 'dagar'} kvar). Lägg till en betalmetod för fortsatt tillgång.`;
          showUpgradeButton = true;
          isInfoBanner = true;
        }
      }
      break;

    case SubscriptionType.EXPIRED_TRIAL:
      message = "Din testperiod har gått ut. Starta prenumeration för att fortsätta använda alla funktioner.";
      showUpgradeButton = true;
      break;

    case SubscriptionType.CANCELED_PAYING:
      message = "Din prenumeration har upphört. Förnya din prenumeration för att fortsätta använda alla funktioner.";
      showUpgradeButton = true;
      break;

    case SubscriptionType.SCHEDULED_CANCELLATION:
      if (subscription.currentPeriodEnd) {
        const endDate = new Date(subscription.currentPeriodEnd.seconds * 1000).toLocaleDateString("sv-SE", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        message = `Din prenumeration är uppsagd och kommer att upphöra den ${endDate}. Förnya för att fortsätta använda alla funktioner.`;
        showUpgradeButton = true;
      }
      break;

    case SubscriptionType.INCOMPLETE:
      // Incomplete (including incomplete_expired) - trial ended without payment
      message = subscriptionInfo.message;
      showUpgradeButton = true;
      break;

    case SubscriptionType.PAST_DUE:
    case SubscriptionType.UNPAID:
      message = subscriptionInfo.message;
      showUpgradeButton = true;
      break;

    default:
      if (subscriptionInfo.needsUpgrade) {
        message = subscriptionInfo.message;
        showUpgradeButton = true;
      }
      break;
  }

  if (!message) {
    return null;
  }

  const bannerColors = isInfoBanner
    ? "bg-gradient-to-r from-blue-500 to-indigo-600 border-b border-blue-400/20"
    : "bg-gradient-to-r from-[#667eea] to-[#764ba2] border-b border-[#5a6fd8]/20";

  const buttonColors = isInfoBanner
    ? "bg-white text-blue-600 hover:bg-blue-50"
    : "bg-white text-[#667eea] hover:bg-white/90";

  return (
    <div className={bannerColors}>
      <div className="max-w-screen-2xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <AlertCircle className="h-4 w-4 text-white flex-shrink-0" />
            <span className="text-sm text-white/95 leading-none">{message}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showUpgradeButton && (
              <Link
                to="/business"
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${buttonColors}`}
              >
                {subscriptionInfo.callToAction}
              </Link>
            )}
            <button
              onClick={() => setIsDismissed(true)}
              className="p-1 hover:bg-white/10 rounded transition-colors text-white/80 hover:text-white"
              aria-label="Stäng"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

